import type { BaseClient } from '../../http/BaseClient.js';
import type {
  ChatBudget,
  EffectiveChatBudget,
  OrgBudget,
  ScopedBudget,
  UpdateChatBudgetRequest,
  UpdateCostLimitRequest,
  UpdateOrgBudgetRequest,
  UpdateScopedBudgetRequest,
} from './types.js';

// Backend routes hang off the root authed group — see internal/http/routes/budgets
// in cloud-agent-backend. The backend resolves project_id → sandbox_id mapping
// and handles RBAC; the frontend just hands it the scope ids.
const BASE = '/budgets';

export class BudgetsClient {
  constructor(private readonly http: BaseClient) {}

  // ── org ──
  getOrg(): Promise<{ budget: OrgBudget }> {
    return this.http.get(`${BASE}/org`);
  }
  updateOrg(body: UpdateOrgBudgetRequest): Promise<{ budget: OrgBudget; message: string }> {
    return this.http.put(`${BASE}/org`, { body });
  }

  // ── project (workspace) ──
  getProject(projectId: string): Promise<{ budget: ScopedBudget }> {
    return this.http.get(`${BASE}/project/${encodeURIComponent(projectId)}`);
  }
  updateProject(projectId: string, body: UpdateScopedBudgetRequest): Promise<{ budget: ScopedBudget; message: string }> {
    return this.http.put(`${BASE}/project/${encodeURIComponent(projectId)}`, { body });
  }

  // ── sandbox (repo) ──
  getSandbox(sandboxId: string): Promise<{ budget: ScopedBudget }> {
    return this.http.get(`${BASE}/sandbox/${encodeURIComponent(sandboxId)}`);
  }
  updateSandbox(sandboxId: string, body: UpdateScopedBudgetRequest): Promise<{ budget: ScopedBudget; message: string }> {
    return this.http.put(`${BASE}/sandbox/${encodeURIComponent(sandboxId)}`, { body });
  }

  // ── chat session ──
  getChat(chatId: string): Promise<{ budget: ChatBudget }> {
    return this.http.get(`${BASE}/chat/${encodeURIComponent(chatId)}`);
  }
  updateChat(chatId: string, body: UpdateChatBudgetRequest): Promise<{ message: string }> {
    return this.http.put(`${BASE}/chat/${encodeURIComponent(chatId)}`, { body });
  }
  // Resolves chat → sandbox → project → org and returns the first non-zero
  // per-chat USD cap plus the level it came from. Powers the chat header
  // budget badge — UI labels inherited values.
  getEffectiveChat(chatId: string): Promise<EffectiveChatBudget> {
    return this.http.get(`${BASE}/chat/${encodeURIComponent(chatId)}/effective`);
  }

  // ── per-template / per-scheduled USD caps ──
  updateTemplateCostLimit(templateId: string, body: UpdateCostLimitRequest) {
    return this.http.put(`${BASE}/template/${encodeURIComponent(templateId)}/cost_limit`, { body });
  }
  updateScheduleCostLimit(scheduleId: string, body: UpdateCostLimitRequest) {
    return this.http.put(`${BASE}/schedule/${encodeURIComponent(scheduleId)}/cost_limit`, { body });
  }
}
